import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: string;
  email: string;
  name: string;
  company?: string;
  role?: string;
  createdAt: Date;
  lastLoginAt: Date;
}

export interface UserState {
  // Current user
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, company?: string) => Promise<void>;
  logout: () => void;
  updateProfile: (updates: Partial<User>) => void;
  handleGitHubLogin: (token: string, name?: string, email?: string) => void;
  clearError: () => void;
}

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      ...initialState,

      login: async (email: string, _password: string) => {
        set({ isLoading: true, error: null });

        try {
          // Simulate API call - in real implementation, this would call your backend
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // For demo purposes, accept any email/password combination
          const user: User = {
            id: `user_${Date.now()}`,
            email,
            name: email.split('@')[0],
            createdAt: new Date(),
            lastLoginAt: new Date(),
          };

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          set({
            error: 'Login failed. Please check your credentials.',
            isLoading: false,
          });
        }
      },

      register: async (email: string, _password: string, name: string, company?: string) => {
        set({ isLoading: true, error: null });

        try {
          // Simulate API call
          await new Promise((resolve) => setTimeout(resolve, 1000));

          const user: User = {
            id: `user_${Date.now()}`,
            email,
            name,
            company,
            createdAt: new Date(),
            lastLoginAt: new Date(),
          };

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          set({
            error: 'Registration failed. Please try again.',
            isLoading: false,
          });
        }
      },

      logout: () => {
        // Clear local storage tokens (canonical and legacy)
        try {
          localStorage.removeItem('authToken');
          localStorage.removeItem('ce-marking-auth-token');
        } catch {}

        set({
          user: null,
          isAuthenticated: false,
          error: null,
        });
      },

      updateProfile: (updates: Partial<User>) => {
        const { user } = get();
        if (user) {
          set({
            user: { ...user, ...updates },
          });
        }
      },

      handleGitHubLogin: (token: string, name?: string, email?: string) => {
        try {
          const payload = JSON.parse(atob(token));
          const user: User = {
            id: payload.sub,
            name: name || payload.name || 'User',
            email: email || payload.email || '',
            createdAt: new Date(),
            lastLoginAt: new Date(),
          };
          set({ user, isAuthenticated: true, isLoading: false, error: null });
        } catch (error) {
          console.error('Failed to decode or process token', error);
          set({ error: 'Invalid login token received.', isAuthenticated: false });
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'ce-marking-user',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
